import * as R from 'ramda'
// React
import { useRef } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { HallType } from '@weblite/ts-action/hall'
// Logic
import { useHall } from '$logic/hall/react-hook'
import { useRoom } from '$logic/room/react-hook'
import { changeHallInfo } from '$logic/hall'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { closeHallSettings } from '$logic/router'
// Common
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
// Localization
import { areEqualStr } from '$func/text'
import { t } from '$i18n'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
// Component
import { HallSettingsActions } from './hall-settings-actions'
import {
  HallSettingsProfile,
  HallSettingsProfileApiType,
} from './hall-settings-profile'
import { HallSettingsCriticalActions } from './hall-settings-critical-actions'
import { HallSettingsOptions } from './hall-settings-options'

interface Props {
  roomId: string
}

export function HallSettings({ roomId }: Props) {
  const { entity: hall } = useHall(roomId)
  const profileRef = useRef<HallSettingsProfileApiType>(null)
  const room = useRoom(roomId)
  const permissions = useClientUserPermissions(roomId)

  // TODO: show skeleton if needed
  if (!hall) return null

  const isGroup = room?.type === 'GROUP'
  const displayType = isGroup ? t('common.group') : t('common.channel')

  const onConfirmHandler = async () => {
    const { current: profileApi } = profileRef
    if (!profileApi) return

    const name = profileApi.getName()
    const description = profileApi.getDescription()
    const image = profileApi.getImage()
    const patch = {
      ...(!areEqualStr(description, hall.description) && { description }),
      ...(!areEqualStr(name, hall.name) && { name }),
      ...(!areEqualStr(image, hall.image) && { image }),
    }
    if (!R.isEmpty(patch)) {
      showSaveChangesLoadingModal()
      await changeHallInfo(roomId, patch)
      await closeCurrentModal()
    }
    await closeHallSettings()
  }

  return (
    <PageWithAppBar
      appBar={
        permissions?.changeHallInfo ? (
          <AppBarBackTitleConfirm
            title={`${t('common.settings')} ${displayType}`}
            onConfirm={onConfirmHandler}
            confirmDisabled={false}
            onBack={closeHallSettings}
          />
        ) : (
          <AppBarBackTitle
            title={`${t('common.settings')} ${displayType}`}
            onBack={closeHallSettings}
          />
        )
      }
    >
      <SettingsSection>
        <HallSettingsProfile
          hall={hall}
          ref={profileRef}
          editable={Boolean(permissions?.changeHallInfo)}
          isGroup={isGroup}
        />
      </SettingsSection>

      {permissions?.changeHallInfo && (
        <SettingsSection>
          <HallSettingsOptions hall={hall} isGroup={isGroup} />
        </SettingsSection>
      )}

      {permissions?.isAdmin && (
        <SettingsSection>
          <HallSettingsActions
            isGroup={isGroup}
            hallId={roomId}
            permissions={permissions}
          />
        </SettingsSection>
      )}

      {permissions?.isOwner && (
        <SettingsSection>
          <HallSettingsCriticalActions
            hall={hall}
            hallType={room?.type as HallType}
          />
        </SettingsSection>
      )}
    </PageWithAppBar>
  )
}
